import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { onValue, push, ref, update } from 'firebase/database';
import {
  FaEdit,
  FaFileSignature,
  FaPrint,
  FaRegCopy,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import { db } from '../../config/firebase';
import { useAcademySettings } from '../../hooks/useAcademySettings';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import {
  WorkspaceEmptyState,
  WorkspaceSection,
  WorkspaceStatusChip,
} from '../ui/Workspace';

const LETTER_TYPES = [
  { value: 'appointment', label: 'Appointment letter' },
  { value: 'service', label: 'Service letter' },
  { value: 'institutional', label: 'Institutional letter' },
];

const STAFF_ROLES = ['instructor', 'demonstrator', 'manager', 'staff'];

const DEFAULT_FORM = {
  staffId: '',
  type: 'appointment',
  position: '',
  appointmentDate: '',
  salary: '',
  salaryCycle: 'month',
  employmentType: 'Full-time',
  reportingTo: '',
  workLocation: '',
  recipientAddress: '',
  signatoryId: '',
  authorizedName: '',
  authorizedDesignation: '',
  authorizedSignatureMethod: 'text',
  authorizedSignatureText: '',
  authorizedSignatureImage: '',
  authorizedSignaturePlacement: { x: 0, y: 0, width: 170 },
  subject: '',
  body: '',
};

const DEFAULT_SIGNATORY_FORM = {
  id: '',
  name: '',
  designation: '',
  signatureMethod: 'text',
  signatureText: '',
  signatureImage: '',
  signaturePlacement: { x: 0, y: 0, width: 170 },
};

const DocumentationSettings = () => {
  const { currentUser } = useAuth();
  const { academy } = useAcademySettings();
  const [users, setUsers] = useState({});
  const [signatoryRecords, setSignatoryRecords] = useState({});
  const [signatoryForm, setSignatoryForm] = useState(DEFAULT_SIGNATORY_FORM);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [selectedLetterId, setSelectedLetterId] = useState('');
  const [editingLetterId, setEditingLetterId] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isSignatoryOpen, setIsSignatoryOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSignatory, setSavingSignatory] = useState(false);
  const [message, setMessage] = useState('');
  const authorDragRef = useRef(null);

  useEffect(() => {
    const unsubUsers = onValue(ref(db, 'users'), (snapshot) => {
      setUsers(snapshot.val() || {});
    });
    const unsubSignatories = onValue(ref(db, 'settings/documentSignatories'), (snapshot) => {
      setSignatoryRecords(snapshot.val() || {});
    });
    return () => {
      unsubUsers();
      unsubSignatories();
    };
  }, []);

  const staff = useMemo(
    () =>
      Object.entries(users)
        .filter(([, user]) => STAFF_ROLES.includes(String(user.role || '').toLowerCase()))
        .map(([id, user]) => ({
          id,
          ...user,
          name:
            `${user.firstName || user.personalDetails?.firstName || ''} ${
              user.lastName || user.personalDetails?.lastName || ''
            }`.trim() || user.displayName || user.email || 'Staff member',
          email: user.email || user.personalDetails?.email || '',
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users],
  );

  const letterList = useMemo(
    () =>
      Object.entries(users)
        .flatMap(([staffId, user]) =>
          Object.entries(user.letters || {}).map(([id, letter]) => ({
            id,
            staffId,
            staffName:
              letter.staffName ||
              `${user.firstName || user.personalDetails?.firstName || ''} ${
                user.lastName || user.personalDetails?.lastName || ''
              }`.trim() ||
              user.displayName ||
              user.email ||
              'Staff member',
            staffEmail: letter.staffEmail || user.email || user.personalDetails?.email || '',
            ...letter,
          })),
        )
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [users],
  );
  const signatories = useMemo(
    () =>
      Object.entries(signatoryRecords)
        .map(([id, signatory]) => ({
          id,
          ...signatory,
          name: signatory.name || 'Authorized person',
          designation: signatory.designation || 'Authorized signatory',
          signaturePlacement: signatory.signaturePlacement || { x: 0, y: 0, width: 170 },
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [signatoryRecords],
  );

  const selectedStaff = staff.find((item) => item.id === form.staffId) || null;
  const previewLetter = buildLetterRecord({
    academy,
    form,
    staff: selectedStaff,
    operator: currentUser,
  });
  const selectedLetter = letterList.find((letter) => letter.id === selectedLetterId) || null;
  const registryStats = useMemo(
    () => ({
      total: letterList.length,
      pending: letterList.filter((letter) => letter.status === 'pending_signature').length,
      signed: letterList.filter((letter) => String(letter.status || '').includes('signed')).length,
      verified: letterList.filter((letter) => String(letter.status || '').includes('verified')).length,
    }),
    [letterList],
  );

  const updateForm = (field, value) => {
    setMessage('');
    setForm((current) => ({ ...current, [field]: value }));
  };

  const selectSignatory = (id) => {
    const signatory = signatories.find((item) => item.id === id);
    setMessage('');
    setForm((current) => ({
      ...current,
      signatoryId: id,
      authorizedName: signatory?.name || '',
      authorizedDesignation: signatory?.designation || '',
      authorizedSignatureMethod: signatory?.signatureMethod || 'text',
      authorizedSignatureText: signatory?.signatureText || signatory?.name || '',
      authorizedSignatureImage: signatory?.signatureImage || '',
      authorizedSignaturePlacement:
        signatory?.signaturePlacement || { x: 0, y: 0, width: 170 },
    }));
  };

  const updateSignatoryForm = (field, value) => {
    setMessage('');
    setSignatoryForm((current) => ({ ...current, [field]: value }));
  };

  const saveSignatory = async () => {
    if (!signatoryForm.name.trim()) {
      setMessage('Enter the authorized person name.');
      return;
    }
    setSavingSignatory(true);
    setMessage('');
    try {
      const signatoryRef = signatoryForm.id
        ? { key: signatoryForm.id }
        : push(ref(db, 'settings/documentSignatories'));
      const record = {
        name: signatoryForm.name.trim(),
        designation: signatoryForm.designation.trim(),
        signatureMethod: signatoryForm.signatureMethod,
        signatureText:
          signatoryForm.signatureMethod === 'text'
            ? (signatoryForm.signatureText || signatoryForm.name).trim()
            : '',
        signatureImage:
          signatoryForm.signatureMethod === 'image' ? signatoryForm.signatureImage : '',
        signaturePlacement: signatoryForm.signaturePlacement,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid || '',
      };
      await update(ref(db, `settings/documentSignatories/${signatoryRef.key}`), record);
      setSignatoryForm(DEFAULT_SIGNATORY_FORM);
      setIsSignatoryOpen(false);
      setMessage('Authorized signatory saved.');
    } catch (error) {
      console.error('Failed to save authorized signatory:', error);
      setMessage(error?.message || 'Could not save this authorized signatory.');
    } finally {
      setSavingSignatory(false);
    }
  };

  const editSignatory = (signatory) => {
    setSignatoryForm({
      id: signatory.id,
      name: signatory.name || '',
      designation: signatory.designation || '',
      signatureMethod: signatory.signatureMethod || 'text',
      signatureText: signatory.signatureText || signatory.name || '',
      signatureImage: signatory.signatureImage || '',
      signaturePlacement: signatory.signaturePlacement || { x: 0, y: 0, width: 170 },
    });
    setIsSignatoryOpen(true);
  };

  const openSignatoryModal = () => {
    setSignatoryForm(DEFAULT_SIGNATORY_FORM);
    setMessage('');
    setIsSignatoryOpen(true);
  };

  const closeSignatoryModal = () => {
    setSignatoryForm(DEFAULT_SIGNATORY_FORM);
    setIsSignatoryOpen(false);
  };

  const handleAuthorizedSignatureUpload = async (event, target = 'form') => {
    const file = event.target.files?.[0];
    if (!file) return;
    setMessage('');
    try {
      const image = await removeLightImageBackground(file);
      if (target === 'signatory') {
        setSignatoryForm((current) => ({
          ...current,
          signatureMethod: 'image',
          signatureImage: image,
        }));
        return;
      }
      setForm((current) => ({
        ...current,
        authorizedSignatureMethod: 'image',
        authorizedSignatureImage: image,
      }));
    } catch (error) {
      console.error('Failed to prepare signature image:', error);
      setMessage('Could not read this signature image. Try another PNG or JPG file.');
    }
  };

  const startAuthorDrag = (event, target = 'form') => {
    event.preventDefault();
    const placement =
      target === 'signatory'
        ? signatoryForm.signaturePlacement
        : form.authorizedSignaturePlacement;
    authorDragRef.current = {
      target,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      original: placement || { x: 0, y: 0, width: 170 },
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveAuthorSignature = (event) => {
    if (!authorDragRef.current) return;
    const deltaX = event.clientX - authorDragRef.current.startX;
    const deltaY = event.clientY - authorDragRef.current.startY;
    const next = {
      ...authorDragRef.current.original,
      x: Math.max(-80, Math.min(80, authorDragRef.current.original.x + deltaX)),
      y: Math.max(-28, Math.min(28, authorDragRef.current.original.y + deltaY)),
    };
    if (authorDragRef.current.target === 'signatory') {
      setSignatoryForm((current) => ({ ...current, signaturePlacement: next }));
      return;
    }
    setForm((current) => ({ ...current, authorizedSignaturePlacement: next }));
  };

  const stopAuthorDrag = () => {
    authorDragRef.current = null;
  };

  const issueLetter = async () => {
    if (!selectedStaff) {
      setMessage('Select a staff member first.');
      return;
    }
    setSaving(true);
    setMessage('');

    try {
      const existingLetter = editingLetterId
        ? letterList.find((letter) => letter.id === editingLetterId)
        : null;
      const letterRef = existingLetter
        ? { key: existingLetter.id }
        : push(ref(db, `users/${selectedStaff.id}/letters`));
      const timestamp = new Date().toISOString();
      const record = {
        ...previewLetter,
        id: letterRef.key,
        status:
          existingLetter?.status && existingLetter.status !== 'pending_signature'
            ? existingLetter.status
            : previewLetter.requiresSignature
            ? 'pending_signature'
            : 'issued',
        createdAt: existingLetter?.createdAt || timestamp,
        createdBy: existingLetter?.createdBy || currentUser?.uid || '',
        createdByName:
          existingLetter?.createdByName ||
          currentUser?.displayName ||
          currentUser?.email ||
          'Admin',
        updatedAt: timestamp,
        updatedBy: currentUser?.uid || '',
      };

      const updates = {
        [`users/${selectedStaff.id}/letters/${letterRef.key}`]: record,
      };

      if (existingLetter && existingLetter.staffId !== selectedStaff.id) {
        updates[`users/${existingLetter.staffId}/letters/${existingLetter.id}`] = null;
      }

      if (form.type === 'appointment') {
        updates[`users/${selectedStaff.id}/appointment`] = {
          appointmentDate: form.appointmentDate,
          position: form.position,
          salary: form.salary,
          salaryCycle: form.salaryCycle,
          employmentType: form.employmentType,
          reportingTo: form.reportingTo,
          workLocation: form.workLocation,
          latestLetterId: letterRef.key,
          updatedAt: record.createdAt,
        };
      }

      await update(ref(db), updates);
      setSelectedLetterId(letterRef.key);
      setEditingLetterId('');
      setIsEditorOpen(false);
      setMessage(
        existingLetter
          ? 'Letter updated.'
          : 'Letter issued. Staff can now review and sign where required.',
      );
    } catch (error) {
      console.error('Failed to issue staff letter:', error);
      setMessage(error?.message || 'Could not issue this letter. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const markVerified = async (letter) => {
    if (!letter) return;
    await update(ref(db, `users/${letter.staffId}/letters/${letter.id}`), {
      verifiedAt: new Date().toISOString(),
      verifiedBy: currentUser?.uid || '',
      verifiedByName: currentUser?.displayName || currentUser?.email || 'Admin',
      status: letter.status === 'signed' ? 'verified_signed' : 'verified',
    });
  };

  const copySigningLink = async (letter) => {
    if (!letter) return;
    const url = `${window.location.origin}/staff/letters/${letter.id}`;
    await navigator.clipboard?.writeText(url);
    setMessage('Signing link copied.');
  };

  const printLetter = (letter) => {
    setSelectedLetterId(letter.id);
    setTimeout(() => window.print(), 50);
  };

  const editLetter = (letter) => {
    setSelectedLetterId(letter.id);
    setEditingLetterId(letter.id);
    setIsEditorOpen(true);
    setMessage('Editing existing letter.');
    setForm({
      staffId: letter.staffId || '',
      type: letter.type || 'appointment',
      position: letter.position || '',
      appointmentDate: toDateInputValue(letter.appointmentDate),
      salary: letter.salary || '',
      salaryCycle: letter.salaryCycle || 'month',
      employmentType: letter.employmentType || 'Full-time',
      reportingTo: letter.reportingTo || '',
      workLocation: letter.workLocation || '',
      recipientAddress: letter.recipientAddress || '',
      signatoryId: letter.signatoryId || '',
      authorizedName: letter.authorizedName || '',
      authorizedDesignation: letter.authorizedDesignation || '',
      authorizedSignatureMethod: letter.authorizedSignatureMethod || 'text',
      authorizedSignatureText: letter.authorizedSignatureText || letter.authorizedName || '',
      authorizedSignatureImage: letter.authorizedSignatureImage || '',
      authorizedSignaturePlacement:
        letter.authorizedSignaturePlacement || { x: 0, y: 0, width: 170 },
      subject: letter.subject || '',
      body: letter.body || '',
    });
  };

  const cancelEdit = () => {
    setEditingLetterId('');
    setForm(DEFAULT_FORM);
    setIsEditorOpen(false);
    setMessage('');
  };

  const openNewLetter = () => {
    setEditingLetterId('');
    const defaultSignatory = signatories[0];
    setForm(
      defaultSignatory
        ? {
            ...DEFAULT_FORM,
            signatoryId: defaultSignatory.id,
            authorizedName: defaultSignatory.name || '',
            authorizedDesignation: defaultSignatory.designation || '',
            authorizedSignatureMethod: defaultSignatory.signatureMethod || 'text',
            authorizedSignatureText:
              defaultSignatory.signatureText || defaultSignatory.name || '',
            authorizedSignatureImage: defaultSignatory.signatureImage || '',
            authorizedSignaturePlacement:
              defaultSignatory.signaturePlacement || { x: 0, y: 0, width: 170 },
          }
        : DEFAULT_FORM,
    );
    setMessage('');
    setIsEditorOpen(true);
  };

  const deleteLetter = async (letter) => {
    if (!letter) return;
    if (!window.confirm(`Delete ${getLetterTypeLabel(letter.type)} for ${letter.staffName}?`)) {
      return;
    }

    const updates = {
      [`users/${letter.staffId}/letters/${letter.id}`]: null,
    };
    if (users[letter.staffId]?.appointment?.latestLetterId === letter.id) {
      updates[`users/${letter.staffId}/appointment/latestLetterId`] = null;
    }
    await update(ref(db), updates);
    setSelectedLetterId('');
    if (editingLetterId === letter.id) cancelEdit();
    setMessage('Letter deleted.');
  };

  return (
    <div className="space-y-4">
      <style>{printStyles}</style>
      <WorkspaceSection
        title="Documents"
        description="Issue staff letters, send appointment letters for signature, and keep verified copies together."
        status={letterList.length ? 'active' : 'draft'}
        statusLabel={`${letterList.length} records`}
        action={<Button onClick={openNewLetter}>New Document</Button>}
      >
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <DocumentStat label="Generated" value={registryStats.total} />
            <DocumentStat label="Awaiting sign" value={registryStats.pending} tone="amber" />
            <DocumentStat label="Signed" value={registryStats.signed} tone="emerald" />
            <DocumentStat label="Verified" value={registryStats.verified} tone="emerald" />
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <WorkflowStep
              number="01"
              title="Create"
              text="Choose staff, letter type, appointment details, salary and wording."
            />
            <WorkflowStep
              number="02"
              title="Send"
              text="Copy the signing link for appointment letters that need acceptance."
            />
            <WorkflowStep
              number="03"
              title="Store"
              text="Keep the issued, signed, and verified copy in the staff record."
            />
          </div>

          <div className="rounded-2xl border border-stone-200 bg-white p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-stone-950">
                  Authorized signatories
                </p>
                <p className="mt-1 text-xs text-stone-500">
                  Save reusable signing persons, then select one when issuing a document.
                </p>
              </div>
              <Button type="button" size="sm" onClick={openSignatoryModal}>
                Add signatory
              </Button>
            </div>
            {signatories.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {signatories.map((signatory) => (
                  <button
                    key={signatory.id}
                    type="button"
                    onClick={() => editSignatory(signatory)}
                    className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1.5 text-left text-xs font-semibold text-stone-700 hover:bg-white"
                  >
                    {signatory.name}
                    <span className="ml-1 font-normal text-stone-500">
                      {signatory.designation}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
            <div className="flex flex-col gap-2 border-b border-stone-200 bg-stone-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-stone-950">Generated documents</p>
                <p className="text-xs text-stone-500">Select a row to preview and manage the document.</p>
              </div>
            </div>
            {letterList.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-stone-200 text-left text-sm">
                  <thead className="bg-stone-50 text-[10px] font-semibold uppercase tracking-wide text-stone-500">
                    <tr>
                      <th className="px-3 py-2">Staff</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Position</th>
                      <th className="px-3 py-2">Issued</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {letterList.map((letter) => (
                      <Fragment key={letter.id}>
                        <tr
                          onClick={() =>
                            setSelectedLetterId((current) =>
                              current === letter.id ? '' : letter.id,
                            )
                          }
                          className={`cursor-pointer transition hover:bg-stone-50 ${
                            selectedLetter?.id === letter.id ? 'bg-[#fdf2f8]' : 'bg-white'
                          }`}
                        >
                          <td className="max-w-[220px] px-3 py-1.5">
                            <p className="truncate font-semibold leading-5 text-stone-950">
                              {letter.staffName || 'Staff member'}
                            </p>
                            <p className="truncate text-xs leading-4 text-stone-500">
                              {letter.staffEmail || 'No email'}
                            </p>
                          </td>
                          <td className="px-3 py-1.5 text-stone-700">
                            {getLetterTypeLabel(letter.type)}
                          </td>
                          <td className="px-3 py-1.5 text-stone-700">
                            {letter.position || 'Not set'}
                          </td>
                          <td className="px-3 py-1.5 text-stone-600">
                            {formatShortDate(letter.createdAt || letter.issuedDate)}
                          </td>
                          <td className="px-3 py-1.5">
                            <WorkspaceStatusChip
                              status={letter.status}
                              label={formatStatus(letter.status)}
                            />
                          </td>
                        </tr>
                        {selectedLetter?.id === letter.id ? (
                          <tr className="bg-stone-50">
                            <td colSpan={5} className="px-3 py-3">
                              <LetterPreviewPanel
                                letter={letter}
                                signingUrl={`${window.location.origin}/staff/letters/${letter.id}`}
                                onPrint={() => printLetter(letter)}
                                onEdit={() => editLetter(letter)}
                                onCopy={() => copySigningLink(letter)}
                                onVerify={() => markVerified(letter)}
                                onDelete={() => deleteLetter(letter)}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <WorkspaceEmptyState
                title="No letters issued"
                message="Issue the first staff document using the form above."
                className="py-7"
              />
            )}
          </div>

        </div>
      </WorkspaceSection>

      {message && !isEditorOpen ? (
        <p className="rounded-xl bg-stone-100 px-3 py-2 text-sm font-semibold text-stone-700">
          {message}
        </p>
      ) : null}

      <Modal
        isOpen={isSignatoryOpen}
        onClose={closeSignatoryModal}
        panelClassName="inline-block w-full max-w-3xl rounded-2xl bg-white text-left align-bottom shadow-xl transition-all sm:my-6 sm:align-middle"
      >
        <div className="border-b border-stone-200 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-lg font-semibold text-stone-950">
                {signatoryForm.id ? 'Edit Signatory' : 'Add Signatory'}
              </p>
              <p className="mt-1 text-sm text-stone-500">
                Save the authorized person and their signature placement for letters.
              </p>
            </div>
            <button
              type="button"
              onClick={closeSignatoryModal}
              className="rounded-full p-2 text-stone-500 hover:bg-stone-100 hover:text-stone-900"
              aria-label="Close signatory editor"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="grid gap-3 md:grid-cols-2">
            <Field
              label="Name"
              value={signatoryForm.name}
              onChange={(value) => updateSignatoryForm('name', value)}
              placeholder="Full name"
            />
            <Field
              label="Designation"
              value={signatoryForm.designation}
              onChange={(value) => updateSignatoryForm('designation', value)}
              placeholder="Director / Registrar"
            />
            <Select
              label="Signature"
              value={signatoryForm.signatureMethod}
              onChange={(value) => updateSignatoryForm('signatureMethod', value)}
            >
              <option value="text">Text signature</option>
              <option value="image">Image signature</option>
            </Select>
            {signatoryForm.signatureMethod === 'text' ? (
              <Field
                label="Signature text"
                value={signatoryForm.signatureText}
                onChange={(value) => updateSignatoryForm('signatureText', value)}
                placeholder={signatoryForm.name || 'Name on signature'}
              />
            ) : (
              <label>
                <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Signature image
                </span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => handleAuthorizedSignatureUpload(event, 'signatory')}
                  className="mt-1 block w-full text-xs text-stone-600 file:mr-2 file:rounded-lg file:border-0 file:bg-stone-900 file:px-2 file:py-1.5 file:text-xs file:font-semibold file:text-white"
                />
              </label>
            )}
          </div>

          <SignaturePlacementControl
            image={signatoryForm.signatureImage}
            text={signatoryForm.signatureText || signatoryForm.name}
            method={signatoryForm.signatureMethod}
            placement={signatoryForm.signaturePlacement}
            onDragStart={(event) => startAuthorDrag(event, 'signatory')}
            onDragMove={moveAuthorSignature}
            onDragEnd={stopAuthorDrag}
            onScale={(width) =>
              setSignatoryForm((current) => ({
                ...current,
                signaturePlacement: {
                  ...(current.signaturePlacement || { x: 0, y: 0 }),
                  width,
                },
              }))
            }
          />

          <div className="flex flex-wrap justify-end gap-2 border-t border-stone-200 pt-4">
            <Button type="button" variant="outline" onClick={closeSignatoryModal}>
              Cancel
            </Button>
            <Button type="button" onClick={saveSignatory} disabled={savingSignatory}>
              {savingSignatory ? 'Saving...' : 'Save signatory'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isEditorOpen}
        onClose={cancelEdit}
        panelClassName="inline-block w-full max-w-6xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white text-left align-bottom shadow-xl transition-all sm:my-6 sm:align-middle"
      >
        <div className="border-b border-stone-200 px-5 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-lg font-semibold text-stone-950">
                {editingLetterId ? 'Edit Staff Letter' : 'Issue Staff Letter'}
              </p>
              <p className="mt-1 text-sm text-stone-500">
                Generate appointment, service, and institutional letters with A4-ready formatting.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" onClick={cancelEdit}>
                <FaTimes className="mr-2 h-3.5 w-3.5" />
                Cancel
              </Button>
              <Button onClick={issueLetter} disabled={saving}>
                {saving
                  ? editingLetterId
                    ? 'Updating...'
                    : 'Issuing...'
                  : editingLetterId
                  ? 'Update Letter'
                  : 'Issue Letter'}
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <Select label="Staff member" value={form.staffId} onChange={(value) => updateForm('staffId', value)}>
              <option value="">Select staff</option>
              {staff.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name} - {member.email}
                </option>
              ))}
            </Select>
            <Select label="Letter type" value={form.type} onChange={(value) => updateForm('type', value)}>
              {LETTER_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
            <Field label="Position" value={form.position} onChange={(value) => updateForm('position', value)} />
            <Field label="Appointment date" type="date" value={form.appointmentDate} onChange={(value) => updateForm('appointmentDate', value)} />
            <Field label="Entitled salary" value={form.salary} onChange={(value) => updateForm('salary', value)} placeholder="LKR 85,000" />
            <Select label="Salary cycle" value={form.salaryCycle} onChange={(value) => updateForm('salaryCycle', value)}>
              <option value="month">Per month</option>
              <option value="day">Per day</option>
              <option value="session">Per session</option>
              <option value="hour">Per hour</option>
            </Select>
            <Field label="Employment type" value={form.employmentType} onChange={(value) => updateForm('employmentType', value)} />
            <Field label="Reporting to" value={form.reportingTo} onChange={(value) => updateForm('reportingTo', value)} />
            <Field label="Work location" value={form.workLocation} onChange={(value) => updateForm('workLocation', value)} />
            <Field label="Subject" value={form.subject} onChange={(value) => updateForm('subject', value)} placeholder="Optional custom subject" />
            <label className="md:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                Recipient address
              </span>
              <textarea
                value={form.recipientAddress}
                onChange={(event) => updateForm('recipientAddress', event.target.value)}
                rows={2}
                className="mt-1 block w-full rounded-xl border-stone-300 text-sm focus:border-[#d61f74] focus:ring-[#d61f74]"
                placeholder="Address shown below recipient name"
              />
            </label>
            <Select
              label="Authorized signer"
              value={form.signatoryId}
              onChange={selectSignatory}
            >
              <option value="">Manual signer</option>
              {signatories.map((signatory) => (
                <option key={signatory.id} value={signatory.id}>
                  {signatory.name} - {signatory.designation}
                </option>
              ))}
            </Select>
            <Field
              label="Signer name"
              value={form.authorizedName}
              onChange={(value) => updateForm('authorizedName', value)}
              placeholder="Authorized person"
            />
            <Field
              label="Signer designation"
              value={form.authorizedDesignation}
              onChange={(value) => updateForm('authorizedDesignation', value)}
              placeholder="Designation"
            />
            <Select
              label="Signer signature"
              value={form.authorizedSignatureMethod}
              onChange={(value) => updateForm('authorizedSignatureMethod', value)}
            >
              <option value="text">Text signature</option>
              <option value="image">Image signature</option>
            </Select>
            {form.authorizedSignatureMethod === 'text' ? (
              <Field
                label="Signature text"
                value={form.authorizedSignatureText}
                onChange={(value) => updateForm('authorizedSignatureText', value)}
                placeholder={form.authorizedName || 'Name on signature'}
              />
            ) : (
              <label>
                <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Signature image
                </span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => handleAuthorizedSignatureUpload(event, 'form')}
                  className="mt-1 block w-full text-xs text-stone-600 file:mr-2 file:rounded-lg file:border-0 file:bg-stone-900 file:px-2 file:py-1.5 file:text-xs file:font-semibold file:text-white"
                />
              </label>
            )}
            <div className="md:col-span-2 xl:col-span-3">
              <SignaturePlacementControl
                image={form.authorizedSignatureImage}
                text={form.authorizedSignatureText || form.authorizedName}
                method={form.authorizedSignatureMethod}
                placement={form.authorizedSignaturePlacement}
                onDragStart={(event) => startAuthorDrag(event, 'form')}
                onDragMove={moveAuthorSignature}
                onDragEnd={stopAuthorDrag}
                onScale={(width) =>
                  setForm((current) => ({
                    ...current,
                    authorizedSignaturePlacement: {
                      ...(current.authorizedSignaturePlacement || { x: 0, y: 0 }),
                      width,
                    },
                  }))
                }
              />
            </div>
            <label className="md:col-span-2 xl:col-span-4">
              <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                Letter body / notes
              </span>
              <textarea
                value={form.body}
                onChange={(event) => updateForm('body', event.target.value)}
                rows={5}
                className="mt-1 block w-full rounded-xl border-stone-300 text-sm focus:border-[#d61f74] focus:ring-[#d61f74]"
                placeholder="Leave blank to use the professional default wording."
              />
            </label>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-stone-50 p-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-stone-950">Document preview</p>
                <p className="text-xs text-stone-500">A4 print layout generated from the settings above.</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => window.print()}>
                <FaPrint className="mr-2 h-3.5 w-3.5" />
                Print preview
              </Button>
            </div>
            <LetterPaper letter={previewLetter} mode="preview" />
          </div>

          {message ? <p className="text-sm font-semibold text-stone-700">{message}</p> : null}
        </div>
      </Modal>
    </div>
  );
};

const LetterPreviewPanel = ({
  letter,
  signingUrl,
  onPrint,
  onEdit,
  onCopy,
  onVerify,
  onDelete,
}) => (
  <div className="space-y-3 rounded-xl border border-stone-200 bg-white p-3">
    <div className="flex flex-col gap-3 rounded-lg border border-stone-200 bg-stone-50 px-3 py-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-base font-semibold text-stone-950">{letter.staffName}</p>
          <WorkspaceStatusChip status={letter.status} label={formatStatus(letter.status)} />
        </div>
        <p className="mt-1 text-sm text-stone-600">
          {getLetterTypeLabel(letter.type)} / {letter.position || 'Position not set'}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onPrint}>
          <FaPrint className="mr-2 h-3.5 w-3.5" />
          Print
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onEdit}
          disabled={letter.status?.includes('signed')}
        >
          <FaEdit className="mr-2 h-3.5 w-3.5" />
          Edit
        </Button>
        {letter.requiresSignature ? (
          <Button type="button" variant="outline" size="sm" onClick={onCopy}>
            <FaRegCopy className="mr-2 h-3.5 w-3.5" />
            Copy link
          </Button>
        ) : null}
        {letter.status?.includes('signed') ? (
          <Button type="button" size="sm" onClick={onVerify}>
            <FaFileSignature className="mr-2 h-3.5 w-3.5" />
            Verify
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onDelete}
          disabled={letter.status?.includes('signed')}
        >
          <FaTrash className="mr-2 h-3.5 w-3.5" />
          Delete
        </Button>
      </div>
    </div>
    {letter.requiresSignature ? (
      <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600">
        Staff signing URL: <span className="font-semibold text-stone-900">{signingUrl}</span>
      </div>
    ) : null}
    <LetterPaper letter={letter} mode="registry" />
  </div>
);

const DocumentStat = ({ label, value, tone = 'stone' }) => {
  const toneClass =
    tone === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : tone === 'amber'
      ? 'bg-amber-50 text-amber-700 ring-amber-200'
      : 'bg-stone-50 text-stone-700 ring-stone-200';

  return (
    <div className={`rounded-xl px-3 py-2 ring-1 ${toneClass}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-75">
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold">{value}</p>
    </div>
  );
};

const WorkflowStep = ({ number, title, text }) => (
  <div className="rounded-xl border border-stone-200 bg-white px-3 py-3">
    <div className="flex items-start gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-950 text-xs font-semibold text-white">
        {number}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-stone-950">{title}</p>
        <p className="mt-1 text-xs leading-5 text-stone-500">{text}</p>
      </div>
    </div>
  </div>
);

const SignaturePlacementControl = ({
  image,
  text,
  method,
  placement = { x: 0, y: 0, width: 170 },
  onDragStart,
  onDragMove,
  onDragEnd,
  onScale,
}) => (
  <div className="rounded-xl border border-stone-200 bg-stone-50 p-2">
    <div
      onPointerDown={method === 'image' && image ? onDragStart : undefined}
      onPointerMove={method === 'image' && image ? onDragMove : undefined}
      onPointerUp={method === 'image' && image ? onDragEnd : undefined}
      onPointerCancel={method === 'image' && image ? onDragEnd : undefined}
      className="relative h-20 overflow-hidden rounded-lg border border-dashed border-stone-300 bg-white"
    >
      <div className="absolute inset-x-4 bottom-5 border-b border-stone-700" />
      {method === 'image' && image ? (
        <img
          src={image}
          alt="Signature preview"
          className="absolute bottom-5 left-8 cursor-move object-contain"
          draggable={false}
          style={{
            width: `${placement.width || 170}px`,
            transform: `translate(${placement.x || 0}px, ${placement.y || 0}px)`,
            filter: 'grayscale(1) contrast(1.45)',
            mixBlendMode: 'multiply',
          }}
        />
      ) : (
        <span className="absolute bottom-6 left-8 font-serif text-xl text-stone-900">
          {text || 'Signature'}
        </span>
      )}
    </div>
    {method === 'image' ? (
      <label className="mt-2 block">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">
          Size
        </span>
        <input
          type="range"
          min="110"
          max="240"
          value={placement.width || 170}
          onChange={(event) => onScale(Number(event.target.value))}
          className="mt-1 w-full accent-[#d61f74]"
        />
      </label>
    ) : null}
  </div>
);

export const LetterPaper = ({ letter, mode = 'preview' }) => (
  <article className={`letter-paper ${mode === 'registry' ? 'print-target' : ''}`}>
    <header className="letter-head">
      <div className="letter-identity">
        {letter.academyLogoURL ? (
          <img
            src={letter.academyLogoURL}
            alt={`${letter.academyName} logo`}
            className="letter-logo"
          />
        ) : (
          <div className="letter-logo-fallback">
            {getInitials(letter.academyName)}
          </div>
        )}
        <div>
          <p className="letter-brand">{letter.academyName}</p>
          <p>{letter.academyAddress || 'Academy address'}</p>
          <p>
            {letter.academyPhone || 'Phone'} / {letter.academyEmail || 'Email'}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="font-semibold uppercase tracking-[0.18em] text-stone-500">
          {getLetterTypeLabel(letter.type)}
        </p>
        <p>{formatDate(letter.issuedDate)}</p>
      </div>
    </header>

    <section className="letter-address">
      <p className="font-semibold text-stone-950">{letter.staffName || 'Staff member'}</p>
      {letter.recipientAddress ? (
        letter.recipientAddress.split('\n').map((line) => (
          <p key={line}>{line}</p>
        ))
      ) : (
        <p>{letter.staffEmail || 'Staff email'}</p>
      )}
    </section>

    <h2>{letter.subject}</h2>
    <div className="letter-body">{renderLetterBody(letter.body)}</div>

    <dl className="letter-facts">
      <div>
        <dt>Position</dt>
        <dd>{letter.position || 'Not specified'}</dd>
      </div>
      <div>
        <dt>Appointment date</dt>
        <dd>{formatDate(letter.appointmentDate)}</dd>
      </div>
      <div>
        <dt>Employment type</dt>
        <dd>{letter.employmentType || 'Not specified'}</dd>
      </div>
      <div>
        <dt>Entitled salary</dt>
        <dd>
          {letter.salary || 'Not specified'}
          {letter.salary ? ` / ${letter.salaryCycle}` : ''}
        </dd>
      </div>
    </dl>

    <footer className="letter-signatures">
      <div>
        <span className="signature-line">
          {letter.authorizedSignatureImage ? (
            <img
              src={letter.authorizedSignatureImage}
              alt={`${letter.authorizedName || 'Authorized person'} signature`}
              className="signature-image"
              style={{
                width: `${letter.authorizedSignaturePlacement?.width || 170}px`,
                transform: `translate(${
                  (letter.authorizedSignaturePlacement?.x || 0) - 72
                }px, ${letter.authorizedSignaturePlacement?.y || 0}px)`,
              }}
            />
          ) : (
            letter.authorizedSignatureText || letter.authorizedName || ''
          )}
        </span>
        <p>{letter.authorizedName || letter.issuedByName || letter.academyName}</p>
        <small>{letter.authorizedDesignation || letter.academyName}</small>
      </div>
      <div>
        <span
          className={letter.signedAt ? 'has-signature signature-line' : 'signature-line'}
        >
          {letter.signatureImage ? (
            <img
              src={letter.signatureImage}
              alt={`${letter.staffName || 'Staff'} signature`}
              className="signature-image"
              style={{
                width: `${letter.signaturePlacement?.width || 170}px`,
                transform: `translate(${(letter.signaturePlacement?.x || 0) - 72}px, ${
                  letter.signaturePlacement?.y || 0
                }px)`,
              }}
            />
          ) : (
            letter.signatureName || ''
          )}
        </span>
        <p>{letter.staffName || 'Staff member'}</p>
        <small>{letter.signedAt ? `Signed ${formatDate(letter.signedAt)}` : 'Pending signature'}</small>
      </div>
    </footer>

    {letter.requiresSignature ? (
      <section className="letter-acceptance">
        <p>Acceptance and digital execution</p>
        <span>
          By signing this appointment letter, the staff member confirms that the
          appointment terms, salary entitlement, reporting line, confidentiality
          obligations, and assigned responsibilities have been reviewed and accepted.
        </span>
        <dl>
          <div>
            <dt>Status</dt>
            <dd>{letter.signedAt ? 'Accepted and signed' : 'Pending staff acceptance'}</dd>
          </div>
          <div>
            <dt>Accepted at</dt>
            <dd>{letter.acceptedAt ? new Date(letter.acceptedAt).toLocaleString() : 'Pending'}</dd>
          </div>
          <div>
            <dt>Evidence</dt>
            <dd>
              {letter.acceptanceEvidence?.device
                ? `${letter.acceptanceEvidence.device.browser || 'Browser'} / ${
                    letter.acceptanceEvidence.device.platform || 'Device'
                  }`
                : 'Recorded on platform when signed'}
            </dd>
          </div>
        </dl>
      </section>
    ) : null}
  </article>
);

const renderLetterBody = (body = '') => {
  const blocks = [];
  let list = [];
  const flushList = () => {
    if (!list.length) return;
    blocks.push(
      <ul key={`list-${blocks.length}`}>
        {list.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>,
    );
    list = [];
  };

  body.split('\n').forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }
    if (/^[-*]\s+/.test(trimmed)) {
      list.push(trimmed.replace(/^[-*]\s+/, ''));
      return;
    }
    flushList();
    blocks.push(<p key={`p-${index}`}>{trimmed}</p>);
  });
  flushList();
  return blocks;
};

export const buildLetterRecord = ({ academy, form, staff, operator }) => {
  const type = form.type || 'appointment';
  const subject =
    form.subject ||
    (type === 'appointment'
      ? `Appointment as ${form.position || 'Staff Member'}`
      : type === 'service'
      ? 'Service Confirmation Letter'
      : 'Institutional Letter');
  const body = sanitizeLetterBody(form.body) || getDefaultBody(type, form, staff, academy);

  return {
    type,
    requiresSignature: type === 'appointment',
    academyName: academy?.name || 'Brissbella Academy',
    academyLogoURL: academy?.logoURL || '',
    academyAddress: academy?.addressLine || '',
    academyPhone: academy?.contactPhone || '',
    academyEmail: academy?.contactEmail || '',
    issuedDate: new Date().toISOString(),
    staffId: staff?.id || '',
    staffName: staff?.name || '',
    staffEmail: staff?.email || '',
    staffRole: staff?.role || '',
    subject,
    body,
    position: form.position,
    appointmentDate: form.appointmentDate,
    salary: form.salary,
    salaryCycle: form.salaryCycle,
    employmentType: form.employmentType,
    reportingTo: form.reportingTo,
    workLocation: form.workLocation,
    recipientAddress: form.recipientAddress || '',
    signatoryId: form.signatoryId || '',
    authorizedName: form.authorizedName || operator?.displayName || operator?.email || '',
    authorizedDesignation: form.authorizedDesignation || 'Authorized signatory',
    authorizedSignatureMethod: form.authorizedSignatureMethod || 'text',
    authorizedSignatureText:
      form.authorizedSignatureMethod === 'text'
        ? form.authorizedSignatureText || form.authorizedName || operator?.displayName || ''
        : '',
    authorizedSignatureImage:
      form.authorizedSignatureMethod === 'image' ? form.authorizedSignatureImage || '' : '',
    authorizedSignaturePlacement:
      form.authorizedSignaturePlacement || { x: 0, y: 0, width: 170 },
    issuedByName: operator?.displayName || operator?.email || 'Admin',
  };
};

const getDefaultBody = (type, form, staff, academy) => {
  const name = staff?.name || 'the staff member';
  const academyName = academy?.name || 'Brissbella Academy';
  if (type === 'appointment') {
    return [
      `We are pleased to confirm the appointment of ${name} as ${form.position || 'Staff Member'} at ${academyName}, effective ${formatDate(form.appointmentDate)}. This appointment is issued according to the details recorded by the academy and confirms the role, reporting arrangement, payment basis, working location, and professional responsibilities attached to the position.`,
      `This is a ${form.employmentType || 'staff'} appointment. The staff member will report to ${form.reportingTo || 'the academy management'} and will be assigned to ${form.workLocation || 'the academy branch or workspace communicated by management'}. The entitled payment is ${form.salary || 'as agreed'}${form.salary ? ` per ${form.salaryCycle}` : ''}; where payment varies by assigned sessions, the final payable amount will be calculated according to approved sessions, attendance, workload, and academy payment records.`,
      'Duties and responsibilities:',
      '- Deliver assigned classes, demonstrations, practical sessions, assessments, and learner support according to academy standards.',
      '- Prepare class materials, maintain lesson discipline, guide students professionally, and support consistent learner progress.',
      '- Maintain accurate attendance, session, assessment, and student-progress records in the academy systems or approved formats.',
      '- Follow academy policies on conduct, confidentiality, safeguarding, health and safety, equal treatment, and professional communication.',
      '- Coordinate with the reporting manager regarding timetables, course requirements, student concerns, substitutions, and operational updates.',
      '- Protect academy property, learning materials, student information, and institutional reputation at all times.',
      `The appointment is subject to academy policies, assigned schedules, satisfactory performance, confidentiality obligations, and any applicable written agreements or laws. ${academyName} may update duties, class allocations, locations, and reporting requirements in line with operational needs.`,
      'Please review this appointment letter carefully. By accepting and signing through the platform, the staff member confirms that the appointment details, duties, responsibilities, payment basis, reporting line, confidentiality obligations, and institutional expectations have been read, understood, and accepted.',
    ].join('\n');
  }
  if (type === 'service') {
    return [
      `This is to certify that ${name} serves at ${academyName} as ${form.position || 'a staff member'}.`,
      `According to academy records, the appointment/service date is ${formatDate(form.appointmentDate)}. This letter is issued upon request for official use.`,
    ].join('\n');
  }
  return [
    `This letter is issued by ${academyName} regarding ${name}.`,
    'The details above are recorded according to the information available in the academy administration system.',
  ].join('\n');
};

const sanitizeLetterBody = (body = '') =>
  String(body)
    .replace(/^(XYZQQRRZZ|XYZ|QQ|RR|ZZ)+/g, '')
    .replace(/MM$/g, '')
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const removeLightImageBackground = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const image = new Image();
      image.onerror = reject;
      image.onload = () => {
        const maxWidth = 640;
        const scale = Math.min(1, maxWidth / image.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const frame = context.getImageData(0, 0, canvas.width, canvas.height);
        for (let index = 0; index < frame.data.length; index += 4) {
          const red = frame.data[index];
          const green = frame.data[index + 1];
          const blue = frame.data[index + 2];
          const brightness = (red + green + blue) / 3;
          if (brightness > 228 && Math.max(red, green, blue) - Math.min(red, green, blue) < 24) {
            frame.data[index + 3] = 0;
          } else {
            const ink = brightness < 120 ? 20 : 55;
            frame.data[index] = ink;
            frame.data[index + 1] = ink;
            frame.data[index + 2] = ink;
          }
        }
        context.putImageData(frame, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const getLetterTypeLabel = (type) =>
  LETTER_TYPES.find((item) => item.value === type)?.label || 'Institutional letter';

const formatStatus = (status = 'draft') =>
  status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDate = (value) => {
  if (!value) return 'Not specified';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-LK', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatShortDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-LK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const toDateInputValue = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const getInitials = (value = '') =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'BA';

const Field = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
  <label>
    <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="mt-1 block w-full rounded-xl border-stone-300 text-sm focus:border-[#d61f74] focus:ring-[#d61f74]"
    />
  </label>
);

const Select = ({ label, value, onChange, children }) => (
  <label>
    <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="mt-1 block w-full rounded-xl border-stone-300 text-sm focus:border-[#d61f74] focus:ring-[#d61f74]"
    >
      {children}
    </select>
  </label>
);

export const printStyles = `
.letter-paper {
  width: min(100%, 210mm);
  min-height: 297mm;
  margin: 0 auto;
  background: white;
  color: #1c1917;
  border: 1px solid #e7e5e4;
  box-shadow: 0 18px 48px -34px rgba(28, 25, 23, 0.45);
  padding: 18mm 17mm;
  font-family: Arial, sans-serif;
  font-size: 12px;
  line-height: 1.55;
}
.letter-head {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  border-bottom: 2px solid #1c1917;
  padding-bottom: 14px;
  margin-bottom: 28px;
}
.letter-identity {
  display: flex;
  align-items: center;
  gap: 14px;
}
.letter-logo,
.letter-logo-fallback {
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  border-radius: 10px;
}
.letter-logo {
  object-fit: contain;
}
.letter-logo-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d6d3d1;
  background: #fafaf9;
  font-size: 18px;
  font-weight: 800;
  color: #1c1917;
}
.letter-brand {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #111827;
}
.letter-address {
  margin-bottom: 24px;
}
.letter-paper h2 {
  font-size: 17px;
  font-weight: 800;
  margin: 0 0 18px;
  color: #111827;
}
.letter-body p {
  margin: 0 0 8px;
  text-align: justify;
}
.letter-body ul {
  margin: 2px 0 10px 18px;
  padding: 0;
  list-style-type: disc;
  list-style-position: outside;
}
.letter-body li {
  display: list-item;
  margin: 0 0 3px;
  padding-left: 2px;
  text-align: justify;
}
.letter-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 26px 0;
  padding: 12px;
  border: 1px solid #e7e5e4;
  background: #fafaf9;
}
.letter-facts dt {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #78716c;
  font-weight: 700;
}
.letter-facts dd {
  margin: 3px 0 0;
  font-weight: 700;
}
.letter-acceptance {
  margin: 18px 0 0;
  padding-top: 8px;
  border-top: 1px solid #e7e5e4;
  color: #78716c;
  font-size: 8px;
  line-height: 1.35;
}
.letter-acceptance p {
  margin: 0 0 3px;
  font-weight: 700;
  color: #57534e;
}
.letter-acceptance span {
  display: block;
}
.letter-acceptance dl {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin: 5px 0 0;
}
.letter-acceptance dt {
  font-size: 7px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #78716c;
  font-weight: 700;
}
.letter-acceptance dd {
  margin: 1px 0 0;
  font-weight: 600;
}
.letter-signatures {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 36px;
  margin-top: 46px;
  align-items: end;
}
.letter-signatures > div {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.letter-signatures span {
  position: relative;
  display: flex;
  width: 54%;
  min-height: 52px;
  align-items: end;
  border-bottom: 1px solid #1c1917;
  font-family: Georgia, serif;
  font-size: 19px;
  color: #111827;
}
.signature-line {
  overflow: visible;
  justify-content: flex-start;
}
.signature-image {
  position: absolute;
  bottom: -3px;
  left: 0;
  display: block;
  max-height: 60px;
  object-fit: contain;
  filter: grayscale(1) contrast(1.45);
  mix-blend-mode: multiply;
}
.letter-signatures p {
  margin: 8px 0 0;
  font-weight: 700;
}
.letter-signatures small {
  color: #78716c;
}
@media print {
  body * {
    visibility: hidden;
  }
  .letter-paper {
    visibility: hidden;
  }
  .letter-paper.print-target, .letter-paper.print-target * {
    visibility: visible;
  }
  .letter-paper.print-target {
    position: absolute;
    inset: 0 auto auto 0;
    width: 210mm;
    min-height: 297mm;
    border: 0;
    box-shadow: none;
    margin: 0;
  }
  @page {
    size: A4;
    margin: 0;
  }
}
`;

export default DocumentationSettings;
